import { ChangeDetectionStrategy, Component, computed, inject, input } from "@angular/core";
import { TranslateModule, TranslateService } from "@ngx-translate/core";
import { environment } from "src/environments/environment";
import { OtaService } from "src/app/shared/services/ota.service";

/**
 * Bloc réutilisable affichant les DEUX versions de l'app, pour diagnostic immédiat (support / staff) :
 * - Version native : le binaire installé ({version} · {build}, via App.getInfo()).
 * - Version OTA    : le bundle JS livré par-dessus (OtaUpdate.getCurrentVersion()), ou « sur version
 *   native » si aucun OTA n'est appliqué.
 *
 * Consommé par le menu de la page événement (couleur héritée du thème via `color`) et par l'écran
 * « Programme bêta ». Les numéros sont sélectionnables (tw-select-all) pour copie ; un aria-label
 * compose les deux versions pour les lecteurs d'écran.
 */
@Component({
	selector: "app-version-block",
	imports: [TranslateModule],
	changeDetection: ChangeDetectionStrategy.OnPush,
	template: `
		<div class="tw-flex tw-flex-col tw-gap-1" [style.color]="color() || null" [attr.aria-label]="ariaLabel()">
			<div class="tw-flex tw-items-baseline tw-gap-1.5" style="font-size: 13px;">
				<i class="material-icons" aria-hidden="true" style="font-size: 14px;">smartphone</i>
				<span aria-hidden="true">{{ "ota.version.native_label" | translate }}</span>
				<span class="tw-min-w-0 tw-flex-1 tw-select-all tw-break-all tw-font-semibold">{{ native() }}</span>
			</div>
			<div class="tw-flex tw-items-baseline tw-gap-1.5" style="font-size: 12px; opacity: 0.7;">
				<i class="material-icons" aria-hidden="true" style="font-size: 14px;">cloud_done</i>
				<span aria-hidden="true">{{ "ota.version.ota_label" | translate }}</span>
				@if (otaVersion(); as v) {
					<span class="tw-min-w-0 tw-flex-1 tw-select-all tw-break-all">{{ v }}</span>
				} @else {
					<span class="tw-min-w-0 tw-flex-1">{{ "ota.version.on_native" | translate }}</span>
				}
			</div>
		</div>
	`
})
export class AppVersionBlockComponent {
	private readonly otaSvc = inject(OtaService);
	private readonly translate = inject(TranslateService);

	// Couleur de texte (héritée du thème événement dans le menu). Vide = couleur par défaut.
	readonly color = input<string>("");

	protected readonly native = computed(() => {
		const n = this.otaSvc.nativeVersion();
		return n ? `${n.version} · ${n.build}` : environment.platform.version;
	});

	protected readonly otaVersion = computed(() => this.otaSvc.currentVersion());

	protected readonly ariaLabel = computed(() => {
		const otaVal = this.otaVersion() ?? this.translate.instant("ota.version.on_native");
		return `${this.translate.instant("ota.version.native_label")} ${this.native()} — ${this.translate.instant(
			"ota.version.ota_label"
		)} ${otaVal}`;
	});
}
